import type { ApiKey, AuditEvent, ConnectedAccount } from "./types";

export interface Me {
  user: { id: string; name: string };
  organizations: {
    id: string;
    name: string;
    slug: string;
    role: string;
    projects: { id: string; name: string; slug: string }[];
  }[];
}

// Parse only fields the console actually returns. Never spread a wire object
// into a dashboard model (keys must never acquire a secret field).
function object(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value))
    throw new Error("Expected an object");
  return value as Record<string, unknown>;
}
function string(value: unknown): string {
  if (typeof value !== "string") throw new Error("Expected a string");
  return value;
}
function nullable(value: unknown): string | null {
  return value === null ? null : string(value);
}
function strings(value: unknown): string[] {
  if (!Array.isArray(value)) throw new Error("Expected an array");
  return value.map(string);
}
function number(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value))
    throw new Error("Expected a number");
  return value;
}
function enumeration<T extends string>(value: unknown, allowed: readonly T[]): T {
  const parsed = string(value);
  const result = allowed.find((item) => item === parsed);
  if (!result) throw new Error("Unexpected enum value");
  return result;
}
export function items<T>(value: unknown, parse: (row: unknown) => T): T[] {
  const rows = object(value).items;
  if (!Array.isArray(rows)) throw new Error("Expected items");
  return rows.map(parse);
}
export function me(value: unknown): Me {
  const row = object(value);
  const user = object(row.user);
  if (!Array.isArray(row.organizations)) throw new Error("Expected organizations");
  return {
    user: { id: string(user.id), name: string(user.name) },
    organizations: row.organizations.map((value) => {
      const org = object(value);
      if (!Array.isArray(org.projects)) throw new Error("Expected projects");
      return {
        id: string(org.id),
        name: string(org.name),
        slug: string(org.slug),
        role: string(org.role),
        projects: org.projects.map((value) => {
          const project = object(value);
          return {
            id: string(project.id),
            name: string(project.name),
            slug: string(project.slug),
          };
        }),
      };
    }),
  };
}
export function account(value: unknown): ConnectedAccount {
  const row = object(value);
  if (!Array.isArray(row.budget)) throw new Error("Expected budget windows");
  const attention = row.attention == null ? undefined : object(row.attention);
  return {
    id: string(row.id),
    provider: string(row.provider),
    handle: string(row.handle),
    status: enumeration(row.status, [
      "connected",
      "syncing",
      "needs_attention",
      "paused",
      "disconnected",
    ]),
    auth: enumeration(row.auth, ["oauth", "imap", "dav", "phone", "account"]),
    lastSyncAt: nullable(row.lastSyncAt),
    attention: attention
      ? {
          code: string(attention.code),
          message: nullable(attention.message),
          since: nullable(attention.since),
        }
      : undefined,
    budget: {
      windows: row.budget.map((value) => {
        const window = object(value);
        return {
          label: string(window.label),
          used: number(window.used),
          limit: number(window.limit),
          resetsAt: string(window.resetsAt),
        };
      }),
    },
  };
}
export function apiKey(value: unknown): ApiKey {
  const row = object(value);
  return {
    id: string(row.id),
    name: string(row.name),
    environment: enumeration(row.environment, ["sandbox", "production"]),
    prefix: string(row.prefix),
    createdAt: string(row.createdAt),
    lastUsedAt: row.lastUsedAt == null ? undefined : string(row.lastUsedAt),
    scopes: row.scopes == null ? undefined : strings(row.scopes),
  };
}
export function auditEvent(value: unknown): AuditEvent {
  const row = object(value);
  return {
    id: string(row.id),
    at: string(row.at),
    actor: string(row.actor),
    actorKind: enumeration(row.actorKind, ["user", "api_key", "system"]),
    action: string(row.action),
    target: string(row.target),
    summary: string(row.summary),
    requestId: string(row.requestId),
  };
}
